import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Smartphone, Download, Apple, PlayCircle, Info, CheckCircle2, ExternalLink, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.christkingdomglobalmarketplace.app';
const APP_STORE_URL = null; // Set once iOS app is submitted. Until then, iOS users use PWA.

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  // iOS covers iPhone / iPad / iPod, plus iPad-on-desktop-user-agent (iPadOS)
  if (/iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

function trackInstall(source, extra = {}) {
  axios.post(`${API}/admin/analytics/track`, {
    event_type: 'install_cta_click',
    data: { source, ...extra },
  }).catch(() => {});
}

export default function InstallAppSection() {
  const [platform, setPlatform] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setInstalled(isStandalone);

    const beforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const openPlayStore = () => {
    trackInstall('play_store', { platform });
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const installWebApp = async () => {
    if (!deferredPrompt) {
      trackInstall('web_app_no_prompt', { platform });
      // No prompt event captured — happens if user already dismissed, already installed,
      // or the browser doesn't yet support the beforeinstallprompt event.
      toast.info(
        "No install prompt available right now. On Chrome/Edge Android: tap the ⋮ menu → 'Install app'. Try refreshing this page first.",
        { duration: 8000 },
      );
      return;
    }
    trackInstall('web_app', { platform });
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    trackInstall('web_app_result', { platform, outcome });
    setDeferredPrompt(null);
  };

  if (installed) {
    return (
      <Card className="border-green-200 bg-green-50" data-testid="install-section-installed">
        <CardContent className="p-5 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-700 shrink-0" />
          <div>
            <p className="font-medium text-green-900">The app is installed</p>
            <p className="text-sm text-green-700/80">You&rsquo;re browsing from the home-screen version — nothing else to do.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="install-app-section">
      <CardContent className="p-0">
        <div className="grid md:grid-cols-2">
          {/* Left: heading */}
          <div className="p-6 md:p-8 bg-gradient-to-br from-primary/10 via-primary/5 to-background flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              <Smartphone className="h-4 w-4" /> Install the app
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-medium mb-3" data-testid="install-heading">
              Christ Kingdom on your home screen.
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              Shop faster, get order updates, and support Christian vendors — right from your phone.
              No account needed to browse.
            </p>
          </div>

          {/* Right: platform-specific CTAs */}
          <div className="p-6 md:p-8 space-y-3">
            {platform === 'android' && (
              <>
                <p className="text-sm text-muted-foreground mb-2" data-testid="install-platform-android">
                  Detected: <strong>Android</strong>
                </p>
                <Button
                  size="lg"
                  className="w-full"
                  onClick={openPlayStore}
                  data-testid="install-play-store-btn"
                >
                  <PlayCircle className="h-5 w-5 mr-2" /> Install from Google Play
                </Button>
                <div className="text-center text-xs text-muted-foreground">— or —</div>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={installWebApp}
                  data-testid="install-web-app-btn"
                >
                  <Download className="h-5 w-5 mr-2" /> Install directly (no store)
                </Button>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5 mt-2">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>Can&rsquo;t use Play Store? The "Install directly" option adds the app to your home screen straight from your browser — same experience, no Play Store account needed.</span>
                </p>
              </>
            )}

            {platform === 'ios' && (
              <>
                <p className="text-sm text-muted-foreground mb-2" data-testid="install-platform-ios">
                  Detected: <strong>iPhone / iPad</strong>
                </p>
                {APP_STORE_URL ? (
                  <Button
                    size="lg"
                    className="w-full"
                    onClick={() => { trackInstall('app_store', { platform }); window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer'); }}
                    data-testid="install-app-store-btn"
                  >
                    <Apple className="h-5 w-5 mr-2" /> Get it on the App Store
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="w-full"
                      onClick={() => setShowIosSteps(true)}
                      data-testid="install-ios-instructions-btn"
                    >
                      <Apple className="h-5 w-5 mr-2" /> Add to Home Screen (Safari)
                    </Button>
                    <p className="text-xs text-muted-foreground flex items-start gap-1.5 mt-2">
                      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>
                        iOS doesn&rsquo;t require an App Store download for us —
                        Safari can add Christ Kingdom to your home screen in 3 taps. Full app experience, no download.
                      </span>
                    </p>
                  </>
                )}
              </>
            )}

            {platform === 'desktop' && (
              <>
                <p className="text-sm text-muted-foreground mb-2" data-testid="install-platform-desktop">
                  Detected: <strong>Desktop</strong> — grab the app on your phone
                </p>
                <div className="bg-muted/40 rounded-xl p-4 flex items-center gap-4">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(PLAY_STORE_URL)}`}
                    alt="QR code — scan to install on Android"
                    className="w-32 h-32 bg-white rounded-lg p-1 shrink-0"
                    data-testid="install-desktop-qr"
                  />
                  <div className="text-sm">
                    <p className="font-medium">Scan with your phone camera</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Opens the Google Play listing on your phone. iPhone users: open{' '}
                      <a href="https://christkingdomglobalmarketplace.com" className="text-primary underline">
                        christkingdomglobalmarketplace.com
                      </a>{' '}
                      in Safari and tap Share → Add to Home Screen.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={openPlayStore}
                  data-testid="install-desktop-play-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" /> Open Play Store listing
                </Button>
              </>
            )}

            {platform === 'unknown' && (
              <Button
                size="lg"
                className="w-full"
                onClick={openPlayStore}
                data-testid="install-fallback-btn"
              >
                <Download className="h-5 w-5 mr-2" /> Get the app
              </Button>
            )}
          </div>
        </div>

        {/* iOS "Add to Home Screen" instructions modal */}
        {showIosSteps && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4"
            onClick={() => setShowIosSteps(false)}
            data-testid="ios-instructions-modal"
          >
            <div
              className="bg-background rounded-2xl max-w-md w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                onClick={() => setShowIosSteps(false)}
                data-testid="ios-instructions-close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2 mb-4">
                <Apple className="h-6 w-6" />
                <h3 className="text-xl font-heading font-medium">Add Christ Kingdom to your iPhone</h3>
              </div>
              <ol className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
                  <div>
                    <p className="font-medium">Open this site in Safari</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      (Chrome / Firefox on iOS can&rsquo;t install web apps — Apple only allows Safari.)
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
                  <div>
                    <p className="font-medium">Tap the Share button</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      The square with an up-arrow, in the middle of the bottom bar.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
                  <div>
                    <p className="font-medium">Scroll down and tap "Add to Home Screen"</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Confirm the name (Christ Kingdom) and tap Add. Done — the app icon is now on your home screen.
                    </p>
                  </div>
                </li>
              </ol>
              <Button
                className="w-full mt-6"
                onClick={() => setShowIosSteps(false)}
                data-testid="ios-instructions-got-it"
              >
                Got it
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
